/**
 * SceneRuntime (spec 012): the canonical canvas player for one scene.
 *
 * Each frame it calls `evaluateFrame` and draws only from the resulting
 * `FrameState` (the scene supplies static styling: colours, transition
 * kinds). It owns the clock, the choice reveal and timer (spec 008) and the
 * audio schedule (spec 007); it declares no timing, easing or keyframe
 * interpolation of its own. `getFrameState()` reports what it drew, which the
 * conformance fixtures hold equal to `evaluateFrame`.
 */

import type {
  ChoiceOption,
  ChoiceSettings,
  FrameState,
  Orientation,
  PlaybackSettings,
  PlayableScene,
  SceneCompleteEvent,
} from '../@types';
import { computeRevealTime, computeSceneAudioCues, exitWindowStart, isExitTransitionActive, transitionProgress, type AudioCue } from '../utils/sceneTiming';
import { AudioCueDriver, type AudioPort } from './audioCues';
import { evaluateFrame, STAGE_SIZES } from './evaluateFrame';
import { createDialogLayoutCache, type DialogBoxRect } from './dialogBox';
import { drawFrame, naturalSize, PLACEHOLDER_FONT_FAMILY, type SceneImage, type SceneImages } from './drawFrame';
import { prepareStageContext, sizeCanvasBuffer } from './canvasBuffer';
import { toPlayableScene, type PlayableSceneInput } from './playableScene';

export type { AudioPort, AudioPlayRequest } from './audioCues';

/** Decoded images by media id (sprite states and background layers). */
export type { SceneImage, SceneImages };
/** The bold sans-serif placeholder labels use (spec 026); defined in drawFrame.ts. */
export { PLACEHOLDER_FONT_FAMILY };

export interface SceneRuntimeOptions {
  /** A `PlayableScene` (other scene shapes are passed through `toPlayableScene`). */
  scene: PlayableScene | PlayableSceneInput;
  canvas: HTMLCanvasElement;
  orientation: Orientation;
  /** Decoded images by media id; a missing image is not drawn and does not stop playback. */
  images?: SceneImages;
  /** Sound output (spec 007); without a port the runtime is silent. */
  audio?: AudioPort<any>;
  /** Default volumes per sound type (spec 007); missing values count as 1. */
  playbackSettings?: Partial<PlaybackSettings>;
  /** Fired exactly once per play: `auto`, `choice` or `timeout` (spec 008). */
  onComplete?: (event: SceneCompleteEvent) => void;
  /** Fired once per play at the reveal time, options in display order. */
  onChoicesReveal?: (choices: ChoiceOption[], settings: ChoiceSettings) => void;
  /** Fired with the playhead (ms) on every frame while playing, after that frame is drawn. */
  onPlayheadUpdate?: (positionMs: number) => void;
}

export class SceneRuntime {
  private scene: PlayableScene;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private orientation: Orientation;
  private images: SceneImages;
  private readonly audio: AudioCueDriver<any> | null;
  private readonly playbackSettings?: Partial<PlaybackSettings>;
  private audioCues: AudioCue[] = [];
  private onPlayheadUpdate?: (positionMs: number) => void;

  private playing = false;
  /**
   * The clock reached where it stops in this play and that frame holds: the
   * play length, or a scene with choices' hold point (spec 015).
   */
  private ended = false;
  private playhead = 0;
  private startTime = 0;
  private rafId: number | null = null;
  private lastFrame: FrameState | null = null;
  /** The dialog boxes of the frame last drawn (stage px). */
  private dialogBoxes: DialogBoxRect[] = [];
  /** Dialog layouts by text, position and stage: a line measures once, not every frame. */
  private readonly dialogLayouts = createDialogLayoutCache();

  /** Called when the scene is resolved, once per play (spec 008). */
  public onComplete?: (event: SceneCompleteEvent) => void;
  /** Called once per play when the playhead reaches the reveal time (spec 008). */
  public onChoicesReveal?: (choices: ChoiceOption[], settings: ChoiceSettings) => void;

  private completeFired = false;
  private choicesRevealed = false;
  private choiceTimerRef: ReturnType<typeof setTimeout> | null = null;
  /** When the running choice timer was (re)armed (performance.now) and for how long. */
  private choiceTimerStartedAt = 0;
  private choiceTimerMs = 0;
  /** Remaining ms of a choice timer paused by `pause()`. */
  private choiceTimerPausedMs: number | null = null;
  /**
   * The exit after a selection or a timeout (spec 015): the scene plays on
   * from `from` while the exit transition runs from `exitFrom` to the play
   * length; the scene completes when it gets there.
   */
  private exiting: {
    /** Scene time of the selection (s). */
    from: number;
    /** Where the exit transition resumes (E = max(s, L − D)). */
    exitFrom: number;
    /** Wall time the exit lasts (L − E). */
    span: number;
    /** performance.now() at e = 0 (moved on by pause/resume). */
    startedAt: number;
    /** e when paused. */
    elapsed: number;
    /** The exit transition's progress last drawn. */
    progress: number;
    exitType: SceneCompleteEvent['exitType'];
    choiceId: string;
  } | null = null;

  constructor(options: SceneRuntimeOptions) {
    this.scene = toPlayableScene(options.scene);
    this.canvas = options.canvas;
    this.ctx = options.canvas.getContext('2d')!;
    this.orientation = options.orientation;
    this.images = options.images ?? {};
    this.audio = options.audio ? new AudioCueDriver(options.audio) : null;
    this.playbackSettings = options.playbackSettings;
    this.onComplete = options.onComplete;
    this.onChoicesReveal = options.onChoicesReveal;
    this.onPlayheadUpdate = options.onPlayheadUpdate;
    this.sizeCanvas();
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /** Start playback from `fromMs`: a new play (completion and reveal reset). */
  play(fromMs: number = 0): void {
    this.cancelFrame();
    this.clearChoiceTimer();
    this.exiting = null;
    this.completeFired = false;
    this.choicesRevealed = false;
    this.ended = false;
    this.playing = true;
    this.playhead = this.clampTime(fromMs);
    this.startTime = performance.now() - this.playhead;
    this.audio?.stopAll();
    this.audioCues = this.audio ? this.computeAudioCues() : [];
    this.render(this.playhead);
    this.requestFrame();
  }

  /**
   * Pause playback, the audio and the choice timer. With `holdLoops` the
   * looping sounds keep playing and the others run to their natural end, as
   * while choices wait (the editor's Preview holding at the reveal, spec 009).
   */
  pause(options: { holdLoops?: boolean } = {}): void {
    if (options.holdLoops) this.audio?.hold();
    else this.audio?.stopAll();
    if (this.exiting && this.playing) this.exiting.elapsed = this.exitElapsed();
    this.playing = false;
    this.cancelFrame();
    if (this.choiceTimerRef !== null) {
      const elapsed = performance.now() - this.choiceTimerStartedAt;
      this.choiceTimerPausedMs = Math.max(0, this.choiceTimerMs - elapsed);
      clearTimeout(this.choiceTimerRef);
      this.choiceTimerRef = null;
    }
  }

  /** Resume after `pause()` from the same playhead (and the rest of the choice timer). */
  resume(): void {
    if (this.playing || this.completeFired) return;
    if (this.exiting) {
      // The exit continues from where pause() froze it (spec 015).
      this.exiting.startedAt = performance.now() - this.exiting.elapsed;
      this.playing = true;
      this.requestFrame();
      return;
    }
    if (this.choiceTimerPausedMs !== null) {
      const remaining = this.choiceTimerPausedMs;
      this.choiceTimerPausedMs = null;
      this.armChoiceTimer(remaining);
    }
    if (this.ended) {
      // Holding the last frame while choices wait: the loops play on.
      this.audio?.update(this.audioCues, this.playhead);
      this.audio?.hold();
      return;
    }
    this.playing = true;
    this.startTime = performance.now() - this.playhead;
    this.requestFrame();
  }

  /**
   * Stop playback, the audio and the choice timer. An exit in progress is
   * cancelled: the scene never completes (spec 015).
   */
  stop(): void {
    if (this.exiting) {
      this.exiting = null;
      this.completeFired = true;
    }
    this.clearChoiceTimer();
    this.audio?.stopAll();
    this.playing = false;
    this.cancelFrame();
  }

  /** Move the playhead to `ms` and render that frame (also while paused). */
  seek(ms: number): void {
    if (this.exiting) return; // input is locked while the exit plays (spec 015)
    this.playhead = this.clampTime(ms);
    this.startTime = performance.now() - this.playhead;
    if (this.playhead < this.holdLimit()) this.ended = false;
    this.render(this.playhead);
  }

  /** Switch orientation: the buffer takes the new stage size and the same t re-renders. */
  setOrientation(orientation: Orientation): void {
    this.orientation = orientation;
    this.sizeCanvas();
    this.render(this.playhead);
  }

  /** Replace the scene (live editing); the playhead stays. */
  updateScene(scene: PlayableScene | PlayableSceneInput): void {
    this.scene = toPlayableScene(scene);
    if (this.audio) this.audioCues = this.computeAudioCues();
    this.render(this.clampTime(this.playhead));
  }

  /** Replace the decoded images (e.g. after a late load) and re-render. */
  setImages(images: SceneImages): void {
    this.images = images;
    this.render(this.playhead);
  }

  /** The state last drawn (evaluated at the playhead when nothing has been drawn yet). */
  getFrameState(): FrameState {
    return this.lastFrame ?? this.evaluate(this.playhead);
  }

  /**
   * The dialog boxes of the frame last drawn, in stage px: one per dialog whose
   * panel is visible (`layoutDialogBox` of its line); `[]` with none. Spec 020
   * stacks the choice panel on them.
   */
  getDialogBoxes(): DialogBoxRect[] {
    return this.dialogBoxes.map((b) => ({ ...b }));
  }

  /** The scene being played, as the player sees it. */
  getScene(): PlayableScene {
    return this.scene;
  }

  getPlayheadPosition(): number {
    return this.playhead;
  }

  isPlaying(): boolean {
    return this.playing;
  }

  /** True from a selection (or timeout) until its exit transition completes the scene (spec 015). */
  isExiting(): boolean {
    return this.exiting !== null;
  }

  /** True once the choice panel has been revealed in the current play. */
  isChoiceRevealed(): boolean {
    return this.choicesRevealed;
  }

  /**
   * The learner selected an option (spec 008): fires `choice` once, after
   * the scene's exit transition (spec 015). Ignored before the reveal, during
   * the exit and after completion; an unknown id is ignored with a console
   * warning.
   */
  selectChoice(choiceId: string): void {
    if (this.completeFired || this.exiting || !this.choicesRevealed) return;
    if (!this.scene.choices.some((c) => c.id === choiceId)) {
      console.warn(`[SceneRuntime] selectChoice: "${choiceId}" is not an option of scene ${this.scene.id}`);
      return;
    }
    this.beginExit('choice', choiceId);
  }

  dispose(): void {
    this.stop();
    this.choiceTimerPausedMs = null;
    this.lastFrame = null;
  }

  // ── Clock ─────────────────────────────────────────────────────────────────

  private clampTime(ms: number): number {
    const t = Number.isFinite(ms) ? ms : 0;
    return Math.min(Math.max(0, t), this.scene.duration);
  }

  /**
   * Where the clock stops on its own in this play (spec 015): the play
   * length L, or for a scene with choices its hold point H (the start of an
   * active exit transition's window). Started or sought beyond H, the clock
   * holds where it is.
   */
  private holdLimit(): number {
    const length = this.scene.duration;
    if (this.scene.choices.length === 0) return length;
    const hold = exitWindowStart(length, this.scene.exitTransition);
    return Math.max(hold, Math.min(this.playhead, length));
  }

  /** The scene time the running clock shows, never past `limit`. */
  private clockTime(limit: number = this.holdLimit()): number {
    return Math.min(this.clampTime(performance.now() - this.startTime), limit);
  }

  private requestFrame(): void {
    this.rafId = requestAnimationFrame(() => this.tick());
  }

  private cancelFrame(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private tick(): void {
    this.rafId = null;
    if (!this.playing) return;
    if (this.exiting) {
      this.exitTick();
      return;
    }
    const length = this.scene.duration;
    const limit = this.holdLimit();
    const t = this.clockTime(limit);
    this.playhead = t;
    this.audio?.update(this.audioCues, t);

    const hasChoices = this.scene.choices.length > 0;
    if (hasChoices && !this.choicesRevealed
        && t >= computeRevealTime(length, this.scene.choiceSettings?.showBeforeEnd, this.scene.exitTransition)) {
      this.revealChoices();
      if (this.exiting) {
        // A selection from onChoicesReveal: its exit drew its first frame and runs its own frames.
        this.onPlayheadUpdate?.(this.playhead);
        return;
      }
      if (!this.playing) {
        // The host paused or stopped from onChoicesReveal (or a choice completed it).
        this.render(t);
        this.onPlayheadUpdate?.(t);
        return;
      }
    }

    this.render(t);
    // After the draw, so a host reads this frame's state and dialog boxes (spec 020).
    this.onPlayheadUpdate?.(t);

    if (t >= limit) {
      // Hold the last frame (spec 012); a scene with choices holds at its
      // hold point, the exit window waiting for a selection (spec 015).
      this.playing = false;
      this.ended = true;
      if (hasChoices) {
        // The choices wait: loops keep playing (spec 007); the learner's
        // selectChoice() or the timer completes the scene.
        if (!this.completeFired) this.audio?.hold();
      } else {
        this.fireComplete('auto', null);
      }
      return;
    }
    this.requestFrame();
  }

  /**
   * The audio cues over the play length (spec 007). A scene with choices
   * has no fixed end, so open-ended sounds neither fade nor stop there.
   */
  private computeAudioCues(): AudioCue[] {
    const length = this.scene.choices.length > 0 ? Infinity : this.scene.duration;
    return computeSceneAudioCues(this.scene, this.playbackSettings, length);
  }

  // ── Choices and completion (spec 008) ─────────────────────────────────────

  private revealChoices(): void {
    const choices = this.scene.choices;
    if (this.choicesRevealed || this.completeFired || choices.length === 0) return;
    this.choicesRevealed = true;
    const settings = this.scene.choiceSettings ?? {};
    if (settings.timer != null && settings.timer > 0) this.armChoiceTimer(settings.timer * 1000);
    this.onChoicesReveal?.(settings.shuffleOptions ? shuffled(choices) : [...choices], settings);
  }

  /** The timer picks the first option in authored order, whatever the shuffle. */
  private armChoiceTimer(ms: number): void {
    const defaultId = this.scene.choices[0]?.id;
    if (defaultId === undefined) return;
    this.choiceTimerStartedAt = performance.now();
    this.choiceTimerMs = ms;
    this.choiceTimerRef = setTimeout(() => {
      this.choiceTimerRef = null;
      if (this.completeFired || this.exiting) return;
      this.beginExit('timeout', defaultId);
    }, ms);
  }

  private clearChoiceTimer(): void {
    if (this.choiceTimerRef !== null) {
      clearTimeout(this.choiceTimerRef);
      this.choiceTimerRef = null;
    }
    this.choiceTimerPausedMs = null;
  }

  // ── The exit after a selection (spec 015) ───────────────────────────────

  /**
   * A selection or a timeout at scene time s: play the rest of the exit
   * transition's window, from E = max(s, L − D) to L, while the scene keeps
   * playing from s; complete when it gets to L. Without an active exit
   * (absent, `'none'`, duration 0), or with nothing left of it, complete now.
   */
  private beginExit(exitType: SceneCompleteEvent['exitType'], choiceId: string): void {
    this.clearChoiceTimer();
    const length = this.scene.duration;
    const exit = this.scene.exitTransition;
    // The scene time of the selection: the clock's own when it runs (never
    // past the hold point), else the held playhead.
    const from = this.playing ? this.clockTime() : this.clampTime(this.playhead);
    const exitFrom = isExitTransitionActive(exit) ? Math.max(from, length - exit.duration) : length;
    const span = length - exitFrom;
    if (!(span > 0)) {
      this.stop();
      this.fireComplete(exitType, choiceId);
      return;
    }
    this.cancelFrame();
    this.exiting = { from, exitFrom, span, startedAt: performance.now(), elapsed: 0, progress: 0, exitType, choiceId };
    // The selection resumes the clock, also when held at the reveal by a host (spec 009).
    this.playing = true;
    this.ended = false;
    this.renderExit(0);
    this.requestFrame();
  }

  private exitElapsed(): number {
    const x = this.exiting!;
    return Math.min(x.span, Math.max(0, performance.now() - x.startedAt));
  }

  private exitTick(): void {
    const x = this.exiting!;
    const e = this.exitElapsed();
    this.renderExit(e);
    this.onPlayheadUpdate?.(this.playhead);
    if (e >= x.span) {
      this.exiting = null;
      this.stop();
      this.fireComplete(x.exitType, x.choiceId);
      return;
    }
    this.requestFrame();
  }

  /** The scene at min(s + e, L), its exit transition at E + e. */
  private renderExit(e: number): void {
    const x = this.exiting!;
    const t = this.clampTime(x.from + e);
    this.playhead = t;
    this.audio?.update(this.audioCues, t);
    x.progress = transitionProgress(this.scene.exitTransition, 'exit', x.exitFrom + e, this.scene.duration);
    this.render(t);
  }

  private fireComplete(exitType: SceneCompleteEvent['exitType'], choiceId: string | null): void {
    if (this.completeFired) return;
    this.completeFired = true;
    this.clearChoiceTimer();
    this.audio?.stopAll();
    this.onComplete?.({ sceneId: this.scene.id, exitType, choiceId });
  }

  // ── Drawing ───────────────────────────────────────────────────────────────

  /** The buffer is the stage size times the device pixel ratio (capped at 3); drawing stays in stage px. */
  private sizeCanvas(): void {
    const stage = STAGE_SIZES[this.orientation] ?? STAGE_SIZES.portrait;
    sizeCanvasBuffer(this.canvas, stage.width, stage.height);
  }

  private evaluate(t: number): FrameState {
    const imageSizes: Record<number, { width: number; height: number }> = {};
    for (const key of Object.keys(this.images)) {
      const img = this.images[Number(key)];
      if (img) imageSizes[Number(key)] = naturalSize(img);
    }
    return evaluateFrame(this.scene, t, this.orientation, { imageSizes });
  }

  /** Draw the frame at t; during the exit after a selection, with the exit's own progress (spec 015). */
  private render(t: number): void {
    const evaluated = this.evaluate(t);
    const frame = this.exiting
      ? { ...evaluated, transition: { ...evaluated.transition, exit: this.exiting.progress } }
      : evaluated;
    this.lastFrame = frame;
    this.dialogBoxes = [];
    try {
      // Resizing the buffer (here or by the host's stage fit) resets the
      // context: map stage px onto whatever buffer there is, every frame.
      prepareStageContext(this.ctx, this.canvas.width / frame.stage.width, this.canvas.height / frame.stage.height);
      this.dialogBoxes = drawFrame(this.ctx, frame, this.scene, this.images, this.dialogLayouts);
    } catch (e) {
      // A drawing failure (a broken image) never stops playback.
      console.warn('[SceneRuntime] draw failed', e);
    }
  }
}

/** Fisher–Yates shuffle into a new array. */
function shuffled<T>(items: readonly T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export default SceneRuntime;
